"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
// Locale-aware `Link`: the plain `next/link` emits a locale-LESS path, so
// every card click paid a middleware redirect round-trip.
import { Link } from "@/i18n/routing";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import type { LjArticle } from "@/fixtures/lj/fixtures";
import { useDragScroll } from "@/hooks/use-drag-scroll";
import { LJ_COLORS, LJ_TYPE } from "@/lib/theme/lj";

interface Props {
  title: string;
  articles: LjArticle[];
}

const GAP_PX = 32;

// Read from `matchMedia` against the SAME breakpoints the card `basis-*`
// classes use (`sm` and `lg`), not a hand-written pixel ladder. The ladder said
// 1024 while globals.css raises `lg` to 1280, so in the 1024–1279 band the
// arrows advanced three cards while 2.25 were visible.
const LG = "(min-width: 80rem)"; // --breakpoint-lg
const SM = "(min-width: 40rem)"; // --breakpoint-sm
function getItemsPerPage() {
  if (typeof window === "undefined") return 3;
  if (window.matchMedia(LG).matches) return 3;
  if (window.matchMedia(SM).matches) return 2;
  return 1;
}

export function ArticleCarousel({ title, articles }: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);

  useEffect(() => {
    const update = () => setItemsPerPage(getItemsPerPage());
    update();
    const lg = window.matchMedia(LG);
    const sm = window.matchMedia(SM);
    lg.addEventListener("change", update);
    sm.addEventListener("change", update);
    return () => {
      lg.removeEventListener("change", update);
      sm.removeEventListener("change", update);
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(articles.length / itemsPerPage));
  const isMobile = itemsPerPage === 1;

  /*
    Grab-and-drag, with the release settling onto a card.

    The rail could only be moved by its arrow buttons or a trackpad gesture —
    grabbing it with the mouse did nothing. QA: "for the carousel, make it
    draggable by cursor and with snapping effect". The shared hook is already in
    production on the stock360 tables, the ej-tech related rail and the ej-tech
    trending strip, so this is another consumer rather than another
    implementation. `snapToChildren` supplies the settling: CSS proximity
    snapping only engages if you happen to stop near a snap point, so a slow
    half-drag just sat where it was left.
  */
  /*
    The release settles on a PAGE, not on a card.

    Every card was a snap target, so a drag stopped wherever the nearest card
    happened to be and the strip drifted out of step with the ‹ / › buttons —
    which move a whole page. The reader then saw a page indicator reading
    "2 / 4" over a view straddling two pages. QA: "the snapping effect should
    be the same as the button control, e.g. mouse slide to next page (page with
    the same number of article items as the prev and next button)".

    Only every `itemsPerPage`-th card carries `data-lj-carousel-page`, so the
    hook's settle — and its flick, which advances one target — work in the same
    units the buttons do. The attribute is re-rendered when the breakpoint
    changes `itemsPerPage`, so the two can never disagree.
  */
  const drag = useDragScroll<HTMLDivElement>(scrollerRef, {
    snapToChildren: true,
    childSelector: "[data-lj-carousel-page]",
  });

  const scrollByPage = (dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const item = el.firstElementChild as HTMLElement | null;
    if (!item) return;
    const slot = item.getBoundingClientRect().width + GAP_PX;
    el.scrollBy({
      left: dir * slot * itemsPerPage,
      behavior: "smooth",
    });
  };

  const scrollToPage = (target: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const item = el.firstElementChild as HTMLElement | null;
    if (!item) return;
    const slot = item.getBoundingClientRect().width + GAP_PX;
    el.scrollTo({ left: target * slot * itemsPerPage, behavior: "smooth" });
  };

  // Sync page indicator from drag/swipe scroll
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const item = el.firstElementChild as HTMLElement | null;
        if (!item) return;
        const slot = item.getBoundingClientRect().width + GAP_PX;
        if (slot <= 0) return;
        const idx = Math.round(el.scrollLeft / (slot * itemsPerPage));
        setPage(Math.max(0, Math.min(totalPages - 1, idx)));
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [totalPages, itemsPerPage]);

  return (
    <section className="mx-auto w-full max-w-[1440px] px-4 lg:px-[120px]">
      {/* Header — title centered, button-pagination right (hidden on mobile) */}
      <header
        className="grid items-center pt-6 pb-5"
        style={{
          gridTemplateColumns: "1fr auto 1fr",
          borderTop: `1px solid ${LJ_COLORS.dividerHair}`,
        }}
      >
        <div />
        <h2
          className="text-center"
          style={{
            ...LJ_TYPE.sectionTitle,
            fontWeight: 400,
            fontSize: 24,
            lineHeight: "30px",
            letterSpacing: "0.14em",
            color: LJ_COLORS.textPrimary,
          }}
        >
          {title.toUpperCase()}
        </h2>
        <div className="hidden sm:flex items-center justify-end gap-3">
          <button
            type="button"
            aria-label="Previous"
            disabled={page === 0}
            onClick={() => scrollByPage(-1)}
            className="grid h-10 w-10 place-items-center transition-opacity hover:opacity-60 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronLeft className="h-7 w-7" strokeWidth={1.5} />
          </button>
          <span
            style={{
              ...LJ_TYPE.byline,
              fontSize: 16,
              lineHeight: "20px",
              fontWeight: 400,
              color: LJ_COLORS.textPrimary,
            }}
          >
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            aria-label="Next"
            disabled={page >= totalPages - 1}
            onClick={() => scrollByPage(1)}
            className="grid h-10 w-10 place-items-center transition-opacity hover:opacity-60 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronRight className="h-7 w-7" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* Carousel — horizontal scroll, snap, 1/2.25/3.25 items per view */}
      <div
        ref={scrollerRef}
        {...drag.dragProps}
        /*
          Touch gets CSS snapping; the mouse keeps the hook's own release
          settle. The drag hook deliberately ignores touch — the platform
          scrolls these natively — so a swipe had nothing to settle it. QA (for
          tablet and mobile): "carousel items: should add snapping effect".

          Two things QA measured on the strip, both fixed here.

          The snap targets are PAGE STARTS — every `itemsPerPage`-th card —
          rather than every card, so a swipe lands where the ‹ / › buttons land
          and the page indicator cannot disagree with what is on screen. QA:
          "the snapping effect should be based on the number of items per
          slide".

          And there is no `scroll-padding`: this scroller has no padding of its
          own (the section around it carries the page gutter), so a
          `scroll-pl-*` inset the settled card by that much and left a gap down
          the left edge. QA: "when snapping, the item is not snapping at the
          start of the item, there is a left padding".
        */
        className={`flex gap-8 overflow-x-auto pb-4 pointer-coarse:snap-x pointer-coarse:snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${drag.dragClass}`}
      >
        {articles.map((article, i) => (
          <CarouselItem
            key={`${article.title}-${i}`}
            article={article}
            pageStart={i % itemsPerPage === 0}
          />
        ))}
      </div>

      {/* Mobile dot pagination */}
      {isMobile ? (
        <div className="mt-3 flex justify-center gap-2 sm:hidden">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to page ${i + 1}`}
              onClick={() => scrollToPage(i)}
              className="h-2 w-2 rounded-full transition-opacity"
              style={{
                backgroundColor: LJ_COLORS.textPrimary,
                opacity: i === page ? 1 : 0.25,
              }}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function CarouselItem({
  article,
  pageStart,
}: {
  article: LjArticle;
  pageStart: boolean;
}) {
  return (
    <Link
      href={article.href}
      data-lj-carousel-page={pageStart ? "" : undefined}
      // Only a PAGE START is a snap target, so a swipe settles where the
      // ‹ / › buttons settle.
      className={`group hkej-tap flex shrink-0 flex-col gap-4 basis-full sm:basis-[calc((100%-4rem)/2.25)] lg:basis-[calc((100%-6rem)/3.25)] ${
        pageStart ? "snap-start" : ""
      }`}
      style={{ color: LJ_COLORS.textPrimary }}
    >
      <div
        className="relative w-full overflow-hidden"
        style={{
          aspectRatio: 4 / 3,
          backgroundColor: LJ_COLORS.bgSubtle,
        }}
      >
        <Image
          src={article.image}
          alt={article.title}
          fill
          className="object-contain object-center"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 45vw, 380px"
          unoptimized
        />
        {"duration" in article ? (
          <div
            className="absolute bottom-3 left-3 grid h-12 w-12 place-items-center rounded-full backdrop-blur-sm"
            style={{ backgroundColor: LJ_COLORS.playButtonBg }}
          >
            <Play
              className="h-5 w-5 translate-x-[1px]"
              style={{ color: LJ_COLORS.textPrimary }}
              fill={LJ_COLORS.textPrimary}
              strokeWidth={0}
            />
          </div>
        ) : null}
      </div>
      <div className="flex flex-col items-start text-left">
        <span
          style={{
            ...LJ_TYPE.eyebrow,
            fontWeight: 400,
            color: LJ_COLORS.textPrimary,
          }}
        >
          {article.category}
        </span>
        <h3
          className="hkej-tap-title line-clamp-2"
          style={{
            ...LJ_TYPE.cardHed,
            color: LJ_COLORS.textPrimary,
            marginTop: 6,
          }}
        >
          {article.title}
        </h3>
        {article.author ? (
          <span
            style={{
              ...LJ_TYPE.byline,
              fontWeight: 400,
              color: LJ_COLORS.textPrimary,
              marginTop: 16,
            }}
          >
            By {article.author}
          </span>
        ) : null}
      </div>
    </Link>
  );
}
